import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { UserServices } from "../../../core/services/UserServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { UserFields, UserListParams, Permission } from "./UserDataTypes";
import "./Users.css";
import UserFilters from "./UserFilters";
import { isEmptyVal } from "../../../utils/helpers";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import AddressCell from "../../components/Elements/AddressCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import UserSingleAction from "./UserSingleAction";
import UserBulkAction from "./UserBulkAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import {
  roleTypeArr,
  statusArr,
  messageTypeArr,
  appConstObj,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { displayField } from "../../../utils/helpers";
import PhoneCell from "../../components/Elements/PhoneCell";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function UsersTab() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<UserFields[]>([]);
  const headerCompRef = useRef({});
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const company = useSelector((state: any) => state.company);
  const modalRef = useRef<ImageModal>(null);
  const [selectedUser, setSelectedUserData] = useState<any>({});
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = JSON.parse(
    localStorage.getItem("loggedInUserDetail") || "{}"
  );
  const [tabView, setTabView] = useState(roleTypeArr.client);

  const initSearchFormData = {
    fname: "",
    lname: "",
    email: "",
    username: "",
    role: "",
    store: "",
    emp_code: "",
    designation: "",
    gender: "",
    religion: "",
    receive_notification: "",
    mobile: "",
    building: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    address_mobile: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] = useState<UserListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);
  const publishPermission = UtilityServices.hasPermission("publish-user");
  const updatePermission = UtilityServices.hasPermission("update-user");
  const deletePermission = UtilityServices.hasPermission("delete-user");
  const permissionObj: Permission = {
    publish: publishPermission,
    update: updatePermission,
    delete: deletePermission,
  };

  const loggedInUserId = UtilityServices.getLoggedInUser("id");
  const loggedInRoleWeight = UtilityServices.getLoggedInUser("roles")[0].weight;

  function dataPermission(user: any) {
    let userId = user?.id;
    let roleWeight = user?.role_weight;
    if (userId == loggedInUserId) return false;
    if (loggedInRoleWeight < roleWeight) return false;
    return true;
  }

  if (checkedKeys.length === data.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (checkedKeys.length > 0 && checkedKeys.length < data.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  async function getUsers(params: UserListParams) {
    try {
      setLoading(true);
      params["type"] = tabView;
      const res = await UserServices.getUsers(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function init() {
    try {
      let params = { role_type: "" };
      const userType = UtilityServices.getLoggedInUser("user_type");
      if (userType != roleTypeArr.backend) {
        params.role_type = roleTypeArr.client;
      }
      setShowCreate(UtilityServices.hasPermission("create-user"));
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  async function deleteUser(user: any) {
    try {
      let res = await UserServices.deleteUser(user?.current?.id);
      setPage(1);
      getUsers(
        getSearchParams({
          type: tabView,
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function publishUser(user: any) {
    try {
      let status =
        user?.current?.status == statusArr.inactive
          ? statusArr.active
          : statusArr.inactive;
      let res = await UserServices.publishUser(user?.current?.id, { status });
      setPage(1);
      getUsers(
        getSearchParams({
          type: tabView,
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkPublishAction(status: string) {
    try {
      let res = await UserServices.publishBulkUser(checkedKeys, { status });
      setPage(1);
      getUsers(
        getSearchParams({
          type: tabView,
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkDeleteAction() {
    try {
      let res = await UserServices.deleteBulkUser(checkedKeys);
      setPage(1);
      getUsers(
        getSearchParams({
          type: tabView,
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function exportUserAction() {
    try {
      await UserServices.exportUsers(getSearchParams());
    } catch (error) {
      console.log(error);
    }
  }

  async function deletePic() {
    try {
      setOpenPictureDelModal(false);

      let res = await UserServices.deleteUserImage(selectedUser.id);
      setPage(1);
      getUsers(
        getSearchParams({
          type: tabView,
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function userPicModal(value: any) {
    if (
      !failedImages.includes(value.large_image_url) &&
      value.large_image_url != null &&
      value.large_image_url !== ""
    ) {
      setSelectedUserData(value);
      modalRef.current?.openModal();
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ company: getCompanyId() },
      ...data,
    };

    let params: UserListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }

    return params;
  }

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  function onPageChange(page: number) {
    setPage(page);
    getUsers(
      getSearchParams({
        type: tabView,
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getUsers(
      getSearchParams({
        type: tabView,
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getUsers(getSearchParams(formData));
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getUsers(
      getSearchParams({
        ...initSearchFormData,
        ...{
          type: tabView,
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterUsers = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getUsers(
        getSearchParams({
          smart_search: smartSearchInput,
          rows: rows,
          type: tabView,
        })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getUsers(
      getSearchParams({
        type: tabView,
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getUsers(
      getSearchParams({
        type: tabView,
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const [showColumns, setShowColumns] = useState([
    { fieldName: t("IMAGE") },
    { fieldName: t("NAME") },
    { fieldName: t("ROLE") },
    { fieldName: t("EMAIL") },
    { fieldName: t("USERNAME") },
    { fieldName: t("PHONE") },
    { fieldName: t("ADDRESS") },
    { fieldName: t("EMPLOYEE_CODE") },
    { fieldName: t("DESIGNATION") },
  ]);

  useEffect(() => {
    getUsers(
      getSearchParams({
        type: tabView,
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
    switch (tabView) {
      case roleTypeArr.client:
        setShowColumns([
          { fieldName: t("IMAGE") },
          { fieldName: t("NAME") },
          { fieldName: t("ROLE") },
          { fieldName: t("COMPANY") },
          { fieldName: t("STORE") },
          { fieldName: t("EMAIL") },
          { fieldName: t("USERNAME") },
          { fieldName: t("PHONE") },
          { fieldName: t("ADDRESS") },
          { fieldName: t("EMPLOYEE_CODE") },
          { fieldName: t("DESIGNATION") },
        ]);
        break;
      case roleTypeArr.backend:
        setShowColumns([
          { fieldName: t("IMAGE") },
          { fieldName: t("NAME") },
          { fieldName: t("ROLE") },
          { fieldName: t("EMAIL") },
          { fieldName: t("USERNAME") },
          { fieldName: t("PHONE") },
          { fieldName: t("ADDRESS") },
          { fieldName: t("EMPLOYEE_CODE") },
          { fieldName: t("DESIGNATION") },
        ]);
        break;

      default:
        break;
    }
  }, [tabView, company]);

  return (
    <div className="users customers">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>{t("USERS")}</h4>
            </FlexboxGrid.Item>
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button
                  appearance="primary"
                  onClick={() => navigate(ContentRoutes.CREATE_USER)}
                >
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </Header>
        <Content>
          <UserFilters
            tabViewSelected={tabView}
            onSubmit={submitSearch}
            onClear={clearSearch}
          />
          <div className="row">
            <UserBulkAction
              checkedKeys={checkedKeys}
              setCheckedKeys={setCheckedKeys}
              onBulkPublish={bulkPublishAction}
              onBulkDelete={bulkDeleteAction}
              onExportUser={exportUserAction}
            />
            <div className="col-md-4">
              <InputGroup>
                <Input
                  className="form-control"
                  placeholder={t("SMART_SEARCH_BY", {
                    entity:
                      t("NAME").toLowerCase() +
                      ", " +
                      t("PHONE").toLowerCase() +
                      ", " +
                      t("OR").toLowerCase() +
                      " " +
                      t("USERNAME").toLowerCase(),
                  })}
                  value={smartSearchInput}
                  onChange={(val: any) => setSmartSearch(val)}
                  onKeyDown={(e: any) => filterUsers(e)}
                />
                <InputGroup.Button
                  title={t("SEARCH")}
                  onClick={(e: any) => filterUsers(e)}
                >
                  <SearchIcon />
                </InputGroup.Button>
                <InputGroup.Button
                  title={t("CLEAR")}
                  onClick={() => clearSmartSearch()}
                >
                  <CloseIcon />
                </InputGroup.Button>
              </InputGroup>
            </div>
          </div>
          {UtilityServices.getLoggedInUser("user_type") ===
          roleTypeArr.backend ? (
            <div>
              <button
                style={{ opacity: tabView === roleTypeArr.client ? 1 : 0.5 }}
                className="tab-button"
                onClick={(e) => setTabView(roleTypeArr.client)}
              >
                Client
              </button>
              <button
                style={{ opacity: tabView === roleTypeArr.backend ? 1 : 0.5 }}
                className="tab-button"
                onClick={(e) => setTabView(roleTypeArr.backend)}
              >
                Backend
              </button>
            </div>
          ) : null}
          <div className={total > 0 ? "usr-tbl-border" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
            >
              <Column width={50} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>
              {showColumns?.length &&
                showColumns.map((field: any, index: number) => {
                  return (
                    <>
                      {field.fieldName === t("IMAGE") ? (
                        <Column width={80} align="center" fixed key={index}>
                          <CustomHeaderCell>{t("IMAGE")}</CustomHeaderCell>
                          <ImageCellFormatter
                            dataKey="large_image_url"
                            rowData={(rowData: any) => rowData}
                            dummyImage={UtilityServices.getLoggedInUser(
                              "user_default_image"
                            )}
                            onFailedImages={getFailedImages}
                            openPictureModal={userPicModal}
                          />
                        </Column>
                      ) : null}

                      {field.fieldName === t("NAME") ? (
                        <Column
                          width={120}
                          sortable
                          resizable
                          fixed
                          key={index}
                        >
                          <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                          <CustomCell dataKey="name">
                            {(rowData: any) => (
                              <span>{displayField(rowData.name)}</span>
                            )}
                          </CustomCell>
                        </Column>
                      ) : null}

                      {field.fieldName === t("ROLE") ? (
                        <Column width={120} resizable fixed key={index}>
                          <CustomHeaderCell>{t("ROLE")}</CustomHeaderCell>
                          <CustomCell dataKey="role_label" />
                        </Column>
                      ) : null}

                      {field.fieldName === t("COMPANY") ? (
                        <Column width={120} resizable fixed key={index}>
                          <CustomHeaderCell>{t("COMPANY")}</CustomHeaderCell>
                          <CustomCell dataKey="company_name" />
                        </Column>
                      ) : null}

                      {field.fieldName === t("STORE") ? (
                        <Column width={120} resizable fixed key={index}>
                          <CustomHeaderCell>{t("STORE")}</CustomHeaderCell>
                          <CustomCell dataKey="store_name" />
                        </Column>
                      ) : null}

                      {field.fieldName === t("EMAIL") ? (
                        <Column width={200} sortable key={index}>
                          <CustomHeaderCell>{t("EMAIL")}</CustomHeaderCell>
                          <CustomCell dataKey="email" />
                        </Column>
                      ) : null}

                      {field.fieldName === t("USERNAME") ? (
                        <Column width={200} sortable key={index}>
                          <CustomHeaderCell>{t("USERNAME")}</CustomHeaderCell>
                          <CustomCell dataKey="username" />
                        </Column>
                      ) : null}

                      {field.fieldName === t("PHONE") ? (
                        <Column width={120} key={index}>
                          <CustomHeaderCell>{t("PHONE")}</CustomHeaderCell>
                          <PhoneCell
                            positionFromRight="30%"
                            rowData={(rowData: any) => rowData}
                          />
                        </Column>
                      ) : null}

                      {field.fieldName === t("ADDRESS") ? (
                        <Column width={160} resizable key={index}>
                          <CustomHeaderCell>{t("ADDRESS")}</CustomHeaderCell>
                          <AddressCell rowData={(rowData: any) => rowData} />
                        </Column>
                      ) : null}

                      {field.fieldName === t("EMPLOYEE_CODE") ? (
                        <Column width={100} resizable key={index}>
                          <CustomHeaderCell>
                            {t("EMPLOYEE_CODE")}
                          </CustomHeaderCell>
                          <CustomCell dataKey="emp_code" />
                        </Column>
                      ) : null}

                      {field.fieldName === t("DESIGNATION") ? (
                        <Column width={100} resizable key={index}>
                          <CustomHeaderCell>
                            {t("DESIGNATION")}
                          </CustomHeaderCell>
                          <CustomCell dataKey="designation" />
                        </Column>
                      ) : null}
                    </>
                  );
                })}

              <Column width={150} fixed="right">
                <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                <Cell>
                  {(rowData) => (
                    <UserSingleAction
                      userData={rowData}
                      onDeleteUser={deleteUser}
                      onPublishUser={publishUser}
                      permission={permissionObj}
                      dataPermission={dataPermission}
                    />
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <ToastContainer />
      </Container>
      <ImageModal
        ref={modalRef}
        modalTitle={t("PROFILE_PIC")}
        heading={t("PROFILE_PIC")}
        body={
          <img
            src={selectedUser.large_image_url}
            className="main-profile-pic"
            alt={t("PROFILE_PIC")}
          />
        }
        removePic={() => setOpenPictureDelModal(true)}
      />
      <AlertModal
        open={openPictureDelModal}
        handleClose={(e: any) => {
          if (e == true) {
            deletePic();
          } else {
            setOpenPictureDelModal(false);
            setSelectedUserData({});
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("PROFILE_PIC").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("DELETE").toLowerCase(),
        })}
      />
    </div>
  );
}

export default UsersTab;
